import { useMutation } from '@tanstack/react-query'
import type { PasswordResetDelivery } from '@volee/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  applyAuthFailure,
  applyProblems,
  formErrorOf,
  isSilentResetFailure,
  resetConfirmProblems,
  resetRequestProblems,
  RESET_CONFIRM_FIELDS,
  RESET_REQUEST_FIELDS,
  type ResetConfirmFields,
  type ResetRequestFields,
} from './auth-fields.ts'
import { useSessionLanding } from './use-sign-in.ts'
import { messages } from '../messages/index.ts'
import { auth } from '../services/auth.ts'

// The two steps of /forgot-password. Both are re-enterable by construction, which is why
// this one IS a route while the first access is a state of /sign-in.

export function usePasswordReset(returnTo: string) {
  const land = useSessionLanding()
  const [delivery, setDelivery] = useState<PasswordResetDelivery | null>(null)
  const requestForm = useForm<ResetRequestFields>({ defaultValues: { email: '' } })
  const confirmForm = useForm<ResetConfirmFields>({
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  })

  const request = useMutation({
    mutationFn: (fields: ResetRequestFields) => auth.password.requestReset(fields.email.trim()),
    onSuccess: (sent: PasswordResetDelivery) => setDelivery(sent),
    onError: (error: unknown) => {
      // An unknown account reaches the same second step: the page must not be an oracle.
      if (isSilentResetFailure(error)) {
        setDelivery({ medium: 'EMAIL', destination: '' })
        return
      }
      applyAuthFailure(requestForm.setError, RESET_REQUEST_FIELDS, error)
    },
  })

  // Two mutations and not one: past the first the password IS changed, so a failure of the
  // second is a different story — and reported as one, or the user retries a spent code.
  const confirm = useMutation({
    mutationFn: (fields: ResetConfirmFields) =>
      auth.password.confirmReset({
        email: requestForm.getValues('email').trim(),
        code: fields.code.trim(),
        newPassword: fields.newPassword,
      }),
    onSuccess: (_result, fields: ResetConfirmFields) => signIn.mutate(fields),
    onError: (error: unknown) => {
      applyAuthFailure(confirmForm.setError, RESET_CONFIRM_FIELDS, error)
    },
  })

  // ConfirmForgotPassword does not authenticate, so one path establishes a session.
  const signIn = useMutation({
    mutationFn: (fields: ResetConfirmFields) =>
      auth.password.signIn({
        email: requestForm.getValues('email').trim(),
        password: fields.newPassword,
      }),
    onSuccess: () => land(returnTo),
    // No input to blame — the new password works, it is this sign-in that did not. The page
    // says so and offers the login, instead of marking a code Cognito has already spent.
    onError: () => {},
  })

  function submitRequest(fields: ResetRequestFields) {
    requestForm.clearErrors()
    const problems = resetRequestProblems(fields, messages.password.required)
    if (applyProblems(requestForm.setError, problems)) return
    request.mutate(fields)
  }

  function submitConfirm(fields: ResetConfirmFields) {
    confirmForm.clearErrors()
    if (applyProblems(confirmForm.setError, resetConfirmProblems(fields, messages.password))) return
    confirm.mutate(fields)
  }

  return {
    /** Null until the code has been asked for: it is what selects the second step. */
    delivery,
    request: {
      form: requestForm,
      submit: submitRequest,
      pending: request.isPending,
      formError: formErrorOf(RESET_REQUEST_FIELDS, request.error),
    },
    confirm: {
      form: confirmForm,
      submit: submitConfirm,
      pending: confirm.isPending || signIn.isPending,
      formError: formErrorOf(RESET_CONFIRM_FIELDS, confirm.error),
    },
    /** True once the password is changed, whether or not the sign-in that follows worked. */
    changed: confirm.isSuccess,
  }
}
